import { selMessage } from "@selector/shared";
import { clone } from "ramda";
import { fixPathFromWindowsToLinux } from "widgets/utils";

import { type ISyncResponse, type IUploadFileResponse } from "@/api/WebApiClient.ts";
import { BrowserTab } from "@/app-utils/browser-tab.ts";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { type Dictionary } from "@/app-utils/type.ts";
import { IndexStatus, type InputModifyProjectFileInput, type ProjectFile, type ProjectFileSearch } from "@/graphql.ts";
import type {
  BrowsingDirectoryFragment,
  BrowsingProjectFileFragment,
  FileDownloadQuery,
} from "@/modules/files/graphql/files-queries.gql.js";
import { isBamFile, isBedFile } from "@/modules/files/utils/file.js";
import { getFolderFromFile } from "@/modules/files/utils/utils.js";
import { type FileEvent, FileStatus } from "@/modules/notifications/utils/types.ts";
import {
  COPY_FILES_FAILURE,
  COPY_FILES_REQUEST,
  COPY_FILES_SUCCESS,
  CREATE_FOLDER_FAILED,
  CUT_FILES_FAILURE,
  CUT_FILES_REQUEST,
  CUT_FILES_SUCCESS,
  DOWNLOAD_FILE_FAILED,
  FILE_SEARCH_FAILED,
  INDEX_BAM_FILE_FAILURE,
  INDEX_BAM_FILE_REQUEST,
  INDEX_BAM_FILE_SUCCESS,
  INDEX_STATUS_CHANGE,
  STORE_FILES_TO_COPY,
  STORE_FILES_TO_CUT,
  UPDATE_INDEXING_STATUS,
  UPDATE_PROJECT_FILE_FAILURE,
  UPDATE_PROJECT_FILE_REQUEST,
  UPDATE_PROJECT_FILE_SUCCESS,
  UPLOAD_RESOURCE_CANCEL,
  UPLOAD_RESOURCE_FAILURE,
  UPLOAD_RESOURCE_PROGRESS,
  UPLOAD_RESOURCE_REMOVE,
  UPLOAD_RESOURCE_REQUEST,
  UPLOAD_RESOURCE_SUCCESS,
} from "@/redux/action-types.ts";
import { FILE_STATUS, TRACK_TYPE } from "@/settings/settings.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { notify } from "../../modules/notifications/notify.js";
import { type IAppState } from "../reducer.js";
import { createReducer, type ReduxAction } from "../redux-utils.js";
import { type IFileUploadState } from "./file-upload.js";

export interface IFileState {
  resourcesUploadLookup: Dictionary<ICollectorResourceUploadState>;
  latestCompletedUpload: ICollectorResourceUploadData | null;

  filesToCopy: BrowsingProjectFileFragment[] | null;
  filesToCut: BrowsingProjectFileFragment[] | null;
  fileOperationInProgress: boolean;

  indexingStatusLookup: Dictionary<IndexStatus>;
}

export type ICollectorResourceUploadState = IFileUploadState<ICollectorResourceUploadData>;

export interface ICollectorResourceUploadData {
  fileName: string;
  fileSize: number;
  creationDate?: number;
  lastModified?: number;
  targetLocation: string;
}

export interface IFileUpload {
  file: File;
  organismId?: number | null;
  description?: string;
}

const initialState: IFileState = {
  resourcesUploadLookup: {},
  latestCompletedUpload: null,

  filesToCopy: null,
  filesToCut: null,
  fileOperationInProgress: false,

  indexingStatusLookup: {},
};

// --- Reducers ---
export default createReducer(initialState, {
  [UPDATE_INDEXING_STATUS]: (state: IFileState, files: ProjectFile[]): IFileState => {
    // update the indexing status in case it has changed since the last file indexing event (e.g. index files deleted)
    const indexingStatusLookup = { ...state.indexingStatusLookup };
    files.forEach((file: ProjectFile) => {
      // keep the "Index complete" special icon that is usually not shown unless the user triggered this indexing
      if (indexingStatusLookup[file.id] && indexingStatusLookup[file.id] !== file.indexingStatus) {
        delete indexingStatusLookup[file.id];
      }
    });

    return {
      ...state,
      indexingStatusLookup: indexingStatusLookup,
    };
  },
  [UPLOAD_RESOURCE_REQUEST]: (
    state: IFileState,
    payload: { request: XMLHttpRequest; uploadData: ICollectorResourceUploadData },
  ): IFileState => {
    const uploadData = payload.uploadData;
    return {
      ...state,
      resourcesUploadLookup: {
        ...state.resourcesUploadLookup,
        [getFullTargetName(uploadData)]: {
          file: { ...uploadData },
          uploadStatus: FILE_STATUS.UPLOAD_PENDING,
          uploadStarted: Date.now(),
          progress: 0,
          request: payload.request,
        },
      },
    };
  },
  [UPLOAD_RESOURCE_SUCCESS]: (state: IFileState, uploadData: ICollectorResourceUploadData): IFileState => {
    return {
      ...state,
      latestCompletedUpload: state.resourcesUploadLookup[getFullTargetName(uploadData)].file,
      resourcesUploadLookup: {
        ...state.resourcesUploadLookup,
        [getFullTargetName(uploadData)]: {
          ...state.resourcesUploadLookup[getFullTargetName(uploadData)],
          uploadStatus: FILE_STATUS.UPLOAD_COMPLETE,
          progress: 100,
        },
      },
    };
  },
  [UPLOAD_RESOURCE_FAILURE]: (
    state: IFileState,
    payload: { error: string; uploadData: ICollectorResourceUploadData },
  ): IFileState => {
    const uploadData = payload.uploadData;
    return {
      ...state,
      resourcesUploadLookup: {
        ...state.resourcesUploadLookup,
        [getFullTargetName(uploadData)]: {
          ...state.resourcesUploadLookup[getFullTargetName(uploadData)],
          uploadStatus: FILE_STATUS.UPLOAD_FAILED,
          progress: 0,
          errors: [payload.error],
        },
      },
    };
  },
  [UPLOAD_RESOURCE_PROGRESS]: (
    state: IFileState,
    payload: { progress: number; uploadData: ICollectorResourceUploadData },
  ): IFileState => {
    const uploadData = payload.uploadData;
    return {
      ...state,
      resourcesUploadLookup: {
        ...state.resourcesUploadLookup,
        [getFullTargetName(uploadData)]: {
          ...state.resourcesUploadLookup[getFullTargetName(uploadData)],
          progress: payload.progress,
        },
      },
    };
  },
  [UPLOAD_RESOURCE_CANCEL]: (state: IFileState, uploadData: ICollectorResourceUploadData): IFileState => {
    state.resourcesUploadLookup[getFullTargetName(uploadData)].request.abort();

    return {
      ...state,
      resourcesUploadLookup: {
        ...state.resourcesUploadLookup,
        [getFullTargetName(uploadData)]: {
          ...state.resourcesUploadLookup[getFullTargetName(uploadData)],
          uploadStatus: FILE_STATUS.UPLOAD_CANCELED,
          errors: ["Upload has been cancelled"],
        },
      },
    };
  },
  /**
   * @param state Current Module State
   * @param payload The files key: ICollectorResourceUploadData.fileName
   * @return Modified Module State
   */
  [UPLOAD_RESOURCE_REMOVE]: (state: IFileState, uploadData: ICollectorResourceUploadData): IFileState => {
    const resourcesUploadLookup = clone(state.resourcesUploadLookup);

    // remove the file
    delete resourcesUploadLookup[getFullTargetName(uploadData)];

    return {
      ...state,
      resourcesUploadLookup,
    };
  },

  [UPDATE_PROJECT_FILE_REQUEST]: (state: IFileState): IFileState => {
    return {
      ...state,
    };
  },
  [UPDATE_PROJECT_FILE_SUCCESS]: (state: IFileState): IFileState => {
    return {
      ...state,
    };
  },
  [UPDATE_PROJECT_FILE_FAILURE]: (state: IFileState): IFileState => {
    return {
      ...state,
    };
  },
  [STORE_FILES_TO_COPY]: (state: IFileState, files: BrowsingProjectFileFragment[]): IFileState => {
    return {
      ...state,
      filesToCopy: files,
      filesToCut: null,
    };
  },
  [STORE_FILES_TO_CUT]: (state: IFileState, files: BrowsingProjectFileFragment[]): IFileState => {
    return {
      ...state,
      filesToCut: files,
      filesToCopy: null,
    };
  },
  [COPY_FILES_REQUEST]: (state: IFileState): IFileState => {
    return {
      ...state,
      fileOperationInProgress: true,
    };
  },
  [COPY_FILES_SUCCESS]: (state: IFileState): IFileState => {
    return {
      ...state,
      fileOperationInProgress: false,
      filesToCopy: null,
    };
  },
  [COPY_FILES_FAILURE]: (state: IFileState): IFileState => {
    return {
      ...state,
      fileOperationInProgress: false,
    };
  },
  [CUT_FILES_REQUEST]: (state: IFileState): IFileState => {
    return {
      ...state,
      fileOperationInProgress: true,
    };
  },
  [CUT_FILES_SUCCESS]: (state: IFileState): IFileState => {
    return {
      ...state,
      fileOperationInProgress: false,
      filesToCut: null,
    };
  },
  [CUT_FILES_FAILURE]: (state: IFileState): IFileState => {
    return {
      ...state,
      fileOperationInProgress: false,
      filesToCut: null, // otherwise user is stuck if he has no write permission on the source folder - he can't get rid of the selection anymore
    };
  },

  [INDEX_STATUS_CHANGE]: (state: IFileState, indexingStatus: FileEvent): IFileState => {
    return {
      ...state,
      indexingStatusLookup: {
        ...state.indexingStatusLookup,
        [indexingStatus.fileId]: indexingStatus.status,
      },
    };
  },
});

// --- Actions ---
export function updateProjectFile(fileInput: InputModifyProjectFileInput): ReduxAction<Promise<ProjectFile>> {
  return async (dispatch, _getState, webApiClient): Promise<ProjectFile> => {
    dispatch({
      type: UPDATE_PROJECT_FILE_REQUEST,
    });
    try {
      const projectFile: ProjectFile = await webApiClient.modifyProjectFile(fileInput);
      dispatch({
        type: UPDATE_PROJECT_FILE_SUCCESS,
        payload: projectFile,
      });

      return projectFile;
    } catch (e) {
      return createErrorHandler(dispatch, {
        actionType: UPDATE_PROJECT_FILE_FAILURE,
        showAlert: false,
        throwError: true,
      })(e);
    }
  };
}

export function validateFileNames(folderId: number, files: File[]): ReduxAction<Promise<string[]>> {
  return async (_dispatch, _getState, webApiClient) => {
    return await webApiClient.fileNameExistsInCollector(
      folderId,
      files.map((file) => file.name),
    );
  };
}

export function createProjectHyperlink(
  folderId: number,
  name: string,
  url: string,
  description?: string,
): ReduxAction<Promise<ProjectFile>> {
  return (_dispatch, _getState, webApiClient) => {
    return webApiClient.createProjectHyperlink(name, url, folderId, description);
  };
}

export function createProjectFolder(folderId: number, name: string): ReduxAction<Promise<boolean>> {
  return (dispatch, _getState, webApiClient) => {
    return webApiClient.createProjectFolder(name, folderId).catch(
      createErrorHandler(dispatch, {
        actionType: CREATE_FOLDER_FAILED,
        showAlert: false,
        throwError: true,
      }),
    );
  };
}

export function uploadCollectorResources(
  currentFolder: BrowsingDirectoryFragment,
  files: IFileUpload[],
): ReduxAction<Promise<any[]>> {
  return (dispatch, _getState, webApiClient) => {
    return Promise.all(
      files.map(async (fileUpload: IFileUpload) => {
        const resourceUploadData: ICollectorResourceUploadData = {
          fileName: fileUpload.file.name,
          fileSize: fileUpload.file.size,
          targetLocation: currentFolder.humanReadablePath || "/",
        };

        const formData: FormData = new FormData();
        formData.append(fileUpload.file.name, fileUpload.file);
        formData.append("folderId", String(currentFolder.id));
        if (fileUpload.organismId) {
          formData.append("organismId", fileUpload.organismId.toString());
        }
        if (fileUpload.description) {
          formData.append("description", fileUpload.description);
        }

        function progressCallback(evt: ProgressEvent) {
          dispatch({
            type: UPLOAD_RESOURCE_PROGRESS,
            payload: {
              uploadData: resourceUploadData,
              progress: Math.floor((evt.loaded * 100) / evt.total),
            },
          });
        }

        function requestStartedCallback(request: XMLHttpRequest) {
          dispatch({
            type: UPLOAD_RESOURCE_REQUEST,
            payload: {
              uploadData: resourceUploadData,
              request: request,
            },
          });
        }

        try {
          const response: IUploadFileResponse = (await webApiClient.uploadFile(
            "/uploadCollector",
            formData,
            progressCallback,
            requestStartedCallback,
          )) as IUploadFileResponse;
          dispatch({
            type: UPLOAD_RESOURCE_SUCCESS,
            payload: resourceUploadData,
          });
          notify.info({
            title: selMessage({ id: "file.upload.successTitle" }),
            children: selMessage({
              id: "file.upload.successMessage",
              values: {
                fileName: response.savedAs,
                targetLocation: resourceUploadData.targetLocation,
              },
            }),
          });
          return response;
        } catch (e) {
          return createErrorHandler(dispatch, {
            actionType: UPLOAD_RESOURCE_FAILURE,
            actionPayload: { uploadData: resourceUploadData, error: e.message },
            alertTitle: "Upload Failed",
            alertMessage: `Could not upload file ${resourceUploadData.fileName} : ${e.message}`,
            throwError: false,
          })(e);
        }
      }),
    );
  };
}

export function clearUploadedCollectorResource(resourceUploadData: ICollectorResourceUploadData): ReduxAction<void> {
  return (dispatch) => {
    dispatch({
      type: UPLOAD_RESOURCE_REMOVE,
      payload: resourceUploadData,
    });
  };
}

export function cancelUploadRequest(resourceUploadData: ICollectorResourceUploadData): ReduxAction<void> {
  return (dispatch) => {
    dispatch({
      type: UPLOAD_RESOURCE_CANCEL,
      payload: resourceUploadData,
    });
  };
}

export function storeFilesToBeCopied(files: BrowsingProjectFileFragment[]) {
  return {
    type: STORE_FILES_TO_COPY,
    payload: files,
  };
}

export function storeFilesToBeCut(files: BrowsingProjectFileFragment[]) {
  return {
    type: STORE_FILES_TO_CUT,
    payload: files,
  };
}

type CopyPasteReturn = {
  sourceFolderId?: number;
  targetFolderId?: number;
} | null;

export function pasteFiles(targetFolder: Partial<ProjectFile>): ReduxAction<Promise<CopyPasteReturn>> {
  return async (dispatch, getState) => {
    const filesToCopy = getState().files.filesToCopy;
    const filesToCut = getState().files.filesToCut;

    if (filesToCopy?.length) {
      return dispatch(copyFiles(targetFolder));
    } else if (filesToCut?.length) {
      return dispatch(cutFiles(targetFolder));
    }

    return null;
  };
}

export function copyFiles(targetFolder: Partial<ProjectFile>): ReduxAction<Promise<CopyPasteReturn>> {
  return async (dispatch, getState, webApiClient) => {
    const filesToCopy = getState().files.filesToCopy;

    dispatch({
      type: COPY_FILES_REQUEST,
    });

    try {
      return await webApiClient
        .copyFiles(
          filesToCopy.map((file) => file.id),
          targetFolder.id,
        )
        .then(() => {
          dispatch({
            type: COPY_FILES_SUCCESS,
          });
          notify.info({
            title: selMessage({ id: "file.copy.successTitle", values: { filesCount: filesToCopy.length } }),
            children: selMessage({
              id: "file.copy.successMessage",
              values: {
                filesCount: filesToCopy.length,
                targetFolderPath: targetFolder.humanReadablePath,
              },
            }),
          });
          // return some info to make it possible to refresh the current folder if necessary
          return {
            targetFolderId: targetFolder.id,
          };
        });
    } catch (e) {
      await createErrorHandler(dispatch, {
        actionType: COPY_FILES_FAILURE,
        alertTitle: "Copy Failed",
        throwError: true,
      })(e);
    }

    return null;
  };
}

export function cutFiles(targetFolder: Partial<ProjectFile>): ReduxAction<Promise<CopyPasteReturn>> {
  return async (dispatch, getState: () => IAppState, webApiClient) => {
    const filesToCut = getState().files.filesToCut;

    dispatch({
      type: CUT_FILES_REQUEST,
    });

    try {
      return await webApiClient
        .moveFiles(
          filesToCut.map((file) => file.id),
          targetFolder.id,
        )
        .then(() => {
          dispatch({
            type: CUT_FILES_SUCCESS,
          });
          notify.info({
            title: selMessage({ id: "file.moved.successTitle", values: { filesCount: filesToCut.length } }),
            children: selMessage({
              id: "file.moved.successMessage",
              values: {
                filesCount: filesToCut.length,
                targetFolderPath: targetFolder.humanReadablePath,
              },
            }),
          });

          // return some info to make it possible to refresh the current folder if necessary
          return {
            sourceFolderId: getFolderFromFile(filesToCut[0]),
            targetFolderId: targetFolder.id,
          };
        });
    } catch (e) {
      await createErrorHandler(dispatch, {
        actionType: CUT_FILES_FAILURE,
        alertTitle: "Move Failed",
        throwError: true,
      })(e);
    }

    return null;
  };
}

export function indexBamFile(fileId: number): ReduxAction<void> {
  return async (dispatch, _getState: () => IAppState, webApiClient) => {
    dispatch({
      type: INDEX_BAM_FILE_REQUEST,
    });

    try {
      await webApiClient.indexBamFile(fileId).then(() => {
        dispatch({
          type: INDEX_BAM_FILE_SUCCESS,
        });
      });
    } catch (e) {
      await createErrorHandler(dispatch, {
        actionType: INDEX_BAM_FILE_FAILURE,
        alertTitle: "Indexing Call Failed",
        throwError: true,
      })(e);
    }
  };
}

export function notifyIndexingStatusChange(indexingStatus: FileEvent) {
  return (dispatch, _getState: () => IAppState, _webApiClient) => {
    const { message, status } = indexingStatus;

    switch (status) {
      case FileStatus.Indexing:
        notify.info({
          title: selMessage({ id: "file.indexing.title" }),
          children: selMessage({
            id: "file.indexing.startedMessage",
            values: { fileName: message },
          }),
        });
        break;
      case FileStatus.Exists:
        notify.info({
          title: selMessage({ id: "file.indexing.title" }),
          children: selMessage({
            id: "file.indexing.finishedMessage",
            values: { fileName: message },
          }),
        });
        break;
      case FileStatus.Failed:
        notify.error({
          title: selMessage({ id: "file.indexing.title" }),
          children: message,
        });
        break;
      default:
    }

    dispatch({
      type: INDEX_STATUS_CHANGE,
      payload: indexingStatus,
    });
  };
}

/**
 * Will send to the server a file selection RegExp.
 * Should be the same as we use in the file utils.
 * @see getFilenameInfo
 * @see getFilePickerSelectableFilter
 */
export function searchProjectFiles(
  terms: string[],
  filterExpression: string,
): ReduxAction<Promise<ProjectFileSearch[]>> {
  return (dispatch, _, webApiClient): Promise<ProjectFileSearch[]> => {
    // search for corresponding project files
    return webApiClient.searchProjectFiles(terms, filterExpression).catch(
      createErrorHandler(dispatch, {
        actionType: FILE_SEARCH_FAILED,
        alertTitle: "File Search Failed",
      }),
    );
  };
}

export function getProjectFilesById(ids: number[]) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<ProjectFile[]> => {
    return webApiClient.getGenomeViewerFilesById(ids).catch(
      createErrorHandler(dispatch, {
        actionPayload: { fileIds: ids },
        alertTitle: "Project File Loading Failed",
        throwError: true,
      }),
    );
  };
}

export function getProjectFilesDataForSendingToGenomeViewer(projectFileIds: number[]): ReduxAction<ProjectFile[]> {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): ProjectFile[] => {
    try {
      const projectFiles: ISyncResponse<ProjectFile[]> =
        webApiClient.getProjectFilesDataForSendingToGenomeViewer(projectFileIds);
      if (projectFiles.errors) {
        throw projectFiles.errors[0];
      }
      return projectFiles.data;
    } catch (e) {
      createErrorHandler(dispatch, {
        alertTitle: "Project files data fetching failed",
        throwError: true,
      })(e);
    }
    return null;
  };
}

/**
 * utils
 */

function getFullTargetName(uploadData: ICollectorResourceUploadData) {
  return fixPathFromWindowsToLinux(`${uploadData.targetLocation}/${uploadData.fileName}`);
}

export function getGenomeViewerMenuItemDisabledHint(file: ProjectFile): string | undefined {
  if (isBamFile(file)) {
    switch (file.indexingStatus) {
      case IndexStatus.Indexing:
        return selMessage({ id: "genomeViewer.file.index.indexing" });
      case IndexStatus.Missing:
      case IndexStatus.Cancelled:
        return selMessage({ id: "genomeViewer.file.index.missing" });
      case IndexStatus.Failed:
        return selMessage({ id: "genomeViewer.file.index.failed" });
    }
  }
  return undefined;
}

export function getFileIndexingMenuItemDisabledHint(file: ProjectFile): string | undefined {
  switch (file.indexingStatus) {
    case IndexStatus.Exists:
      return selMessage({ id: "file.index.exists" });
    case IndexStatus.Indexing:
      return selMessage({ id: "file.index.indexing" });
    case IndexStatus.Missing:
    case IndexStatus.Cancelled:
      return selMessage({ id: "file.index.missing" });
    case IndexStatus.Failed:
      return selMessage({ id: "file.index.failed" });
  }
  return undefined;
}

export function sendFilesToGenomeBrowser(files: ProjectFile[], genomeId?: number) {
  const bamResources = files.filter((file) => isBamFile(file));
  const bedResources = files.filter((file) => isBedFile(file));

  const tracks: string[] = [];
  let bamFilesQueryParam = "";
  let bedFilesQueryParam = "";
  if (bamResources.length) {
    tracks.push(TRACK_TYPE.NGS);
    const bamFileIds: number[] = bamResources.map((file) => file.id);
    bamFilesQueryParam = `&bam_files=${bamFileIds.join(",")}`;
  }
  if (bedResources.length) {
    tracks.push(TRACK_TYPE.BED);
    const bedFileIds: number[] = bedResources.map((file) => file.id);
    bedFilesQueryParam = `&bed_files=${bedFileIds.join(",")}&showGrid=true`;
  }

  // open in new tab (BrowserTab.createUrl takes into account the base path)
  if (genomeId) {
    // case where the genome is known
    window.open(
      BrowserTab.createUrl(
        `/organism/${genomeId}/genome?add_tracks=${tracks.join(",")}${bamFilesQueryParam}${bedFilesQueryParam}`,
      ),
    );
  } else {
    // case where there is no genome in context, we will fetch the sequences list from the BAM files
    window.open(
      BrowserTab.createUrl(
        `/visualizer/genome?add_tracks=${tracks.join(",")}${bamFilesQueryParam}${bedFilesQueryParam}`,
      ),
    );
  }
}

// the following interface corresponds to server-side interface JnlpDownloadInputData, not in API because not part of the graphQL queries/mutations
export interface IJnlpDownloadInputData {
  moduleId: string;
  sessionId?: number;
  fileIds?: number[];
}

export function downloadJnlpFile(jnlpDownloadPayload: IJnlpDownloadInputData): ReduxAction<void> {
  return (dispatch, _getState, webApiClient) => {
    return webApiClient.downloadFileAndWatchCompletion("/modules/jnlp", jnlpDownloadPayload).catch(
      createErrorHandler(dispatch, {
        actionType: DOWNLOAD_FILE_FAILED,
        alertTitle: "Failed to Download the JNLP File",
      }),
    );
  };
}
